import React, { useState } from 'react';
import { Users, Wifi, WifiOff, Clock, Activity, Shield, Eye, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { Spinner } from '../../components/ui/Spinner';
import { useGetSurveillanceQuery, useActiverMaintenanceMutation, useDeconnecterTousMutation, useGetEtatMaintenanceQuery } from '../../services/coreApi';

const ROLE_COLORS: Record<string, string> = {
  super_admin: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  admin: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  agent: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  consultation: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

const OP_COLORS: Record<string, string> = {
  login: 'text-blue-600', logout: 'text-gray-500', creation: 'text-green-600',
  modification: 'text-amber-600', suppression: 'text-red-600', export: 'text-purple-600',
};

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Jamais';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "À l'instant";
  if (mins < 60) return `Il y a ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `Il y a ${days}j`;
}

const SurveillanceTab: React.FC = () => {
  const { data, isLoading, refetch } = useGetSurveillanceQuery(undefined, { pollingInterval: 30000 });
  const { data: maintenanceData, refetch: refetchMaintenance } = useGetEtatMaintenanceQuery();
  const [activerMaintenance] = useActiverMaintenanceMutation();
  const [deconnecterTous] = useDeconnecterTousMutation();
  const [showAllConnexions, setShowAllConnexions] = useState(false);
  const [showHorsLigne, setShowHorsLigne] = useState(false);
  const [maintenanceMsg, setMaintenanceMsg] = useState("L'application est en cours de maintenance. Veuillez patienter.");
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  if (isLoading) return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;
  if (!data) return <p className="text-center py-12 text-gray-400">Données non disponibles</p>;

  return (
    <div className="space-y-6">
      {/* Stats cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: <Wifi size={20} />, label: 'En ligne', value: data.nb_en_ligne, color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/15 border-green-200 dark:border-green-800' },
          { icon: <Users size={20} />, label: 'Utilisateurs', value: data.nb_total, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/15 border-blue-200 dark:border-blue-800' },
          { icon: <Activity size={20} />, label: 'Opérations (7j)', value: data.stats.total_operations_7j, color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-50 dark:bg-purple-900/15 border-purple-200 dark:border-purple-800' },
          { icon: <Clock size={20} />, label: 'Connexions (7j)', value: data.stats.total_connexions_7j, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/15 border-amber-200 dark:border-amber-800' },
        ].map(s => (
          <div key={s.label} className={`rounded-xl border p-4 ${s.bg}`}>
            <div className={`${s.color} mb-1`}>{s.icon}</div>
            <div className={`text-2xl font-extrabold ${s.color}`}>{s.value}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Message d'action */}
      {actionMessage && (
        <div className={`p-3 rounded-lg text-sm ${actionMessage.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-700' : 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-700'}`}>
          {actionMessage.text}
        </div>
      )}

      {/* Panneau de contrôle — Maintenance + Déconnexion */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-amber-300 dark:border-amber-700 p-5">
        <h3 className="font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2 mb-4">
          <Shield size={18} className="text-amber-600" /> Contrôle de la plateforme
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Mode maintenance */}
          <div className={`p-4 rounded-xl border ${maintenanceData?.maintenance ? 'bg-red-50 dark:bg-red-900/15 border-red-300 dark:border-red-700' : 'bg-gray-50 dark:bg-gray-700/30 border-gray-200 dark:border-gray-600'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${maintenanceData?.maintenance ? 'bg-red-500 animate-pulse' : 'bg-green-500'}`} />
                <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">
                  {maintenanceData?.maintenance ? 'Maintenance ACTIVE' : 'Mode normal'}
                </span>
              </div>
            </div>
            <input
              type="text"
              value={maintenanceMsg}
              onChange={(e) => setMaintenanceMsg(e.target.value)}
              placeholder="Message de maintenance..."
              className="w-full px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 mb-3"
            />
            <button
              onClick={async () => {
                try {
                  const activer = !maintenanceData?.maintenance;
                  const res = await activerMaintenance({ activer, message: maintenanceMsg }).unwrap();
                  setActionMessage({ type: 'success', text: res.message });
                  refetchMaintenance();
                } catch (err: any) {
                  setActionMessage({ type: 'error', text: err?.data?.error || 'Erreur' });
                }
              }}
              className={`w-full py-2 text-sm font-bold rounded-lg transition ${maintenanceData?.maintenance
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-red-600 hover:bg-red-700 text-white'
              }`}
            >
              {maintenanceData?.maintenance ? 'Désactiver la maintenance' : 'Activer la maintenance'}
            </button>
            <p className="text-[10px] text-gray-400 mt-2">
              En maintenance, seul le super_admin peut se connecter. Les autres voient le message ci-dessus.
            </p>
          </div>

          {/* Déconnexion forcée */}
          <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-600">
            <div className="flex items-center gap-2 mb-3">
              <WifiOff size={16} className="text-red-500" />
              <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">Déconnexion forcée</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Déconnecte tous les utilisateurs sauf vous. Utile avant une mise à jour.
            </p>
            <button
              onClick={async () => {
                if (!window.confirm('Déconnecter TOUS les utilisateurs (sauf vous) ?')) return;
                try {
                  const res = await deconnecterTous().unwrap();
                  setActionMessage({ type: 'success', text: res.message });
                  refetch();
                } catch (err: any) {
                  setActionMessage({ type: 'error', text: err?.data?.error || 'Erreur' });
                }
              }}
              className="w-full py-2 text-sm font-bold bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition"
            >
              Déconnecter tous les utilisateurs
            </button>
            {data && data.nb_en_ligne > 0 && (
              <p className="text-[10px] text-orange-500 mt-2">
                {data.nb_en_ligne} utilisateur(s) actuellement en ligne
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Refresh */}
      <div className="flex justify-end">
        <button onClick={() => refetch()} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-blue-600 transition">
          <RefreshCw size={13} /> Actualiser (auto: 30s)
        </button>
      </div>

      {/* Utilisateurs en ligne */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <h3 className="font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2 mb-4">
          <div className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" />
          Utilisateurs en ligne ({data.nb_en_ligne})
        </h3>
        {data.en_ligne.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center">Aucun utilisateur en ligne</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.en_ligne.map(u => (
              <div key={u.id} className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/10 rounded-lg border border-green-200 dark:border-green-800">
                <div className="w-10 h-10 rounded-full bg-green-200 dark:bg-green-800 flex items-center justify-center font-bold text-green-700 dark:text-green-300 text-sm">
                  {u.nom.charAt(0) || u.username.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">{u.nom || u.username}</p>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${ROLE_COLORS[u.role] || ''}`}>{u.role}</span>
                    <span className="text-[10px] text-gray-400">{timeAgo(u.derniere_connexion)}</span>
                  </div>
                </div>
                <Wifi size={16} className="text-green-500 shrink-0" />
              </div>
            ))}
          </div>
        )}

        {/* Hors ligne (repliable) */}
        <button onClick={() => setShowHorsLigne(!showHorsLigne)} className="mt-4 flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition">
          {showHorsLigne ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          <WifiOff size={14} />
          Hors ligne ({data.hors_ligne.length})
        </button>
        {showHorsLigne && (
          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {data.hors_ligne.map(u => (
              <div key={u.id} className="flex items-center gap-3 p-2.5 bg-gray-50 dark:bg-gray-700/30 rounded-lg text-sm">
                <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500 text-xs font-bold">
                  {u.nom.charAt(0) || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{u.nom || u.username}</p>
                  <span className="text-[10px] text-gray-400">{timeAgo(u.derniere_connexion)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Activité par utilisateur (7 jours) */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <h3 className="font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2 mb-4">
          <Activity size={18} /> Activité par utilisateur (7 derniers jours)
        </h3>
        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/50 text-xs text-gray-500 dark:text-gray-400 uppercase">
                <th className="px-4 py-2.5 text-left">Utilisateur</th>
                <th className="px-4 py-2.5 text-center">Total</th>
                <th className="px-4 py-2.5 text-center">Connexions</th>
                <th className="px-4 py-2.5 text-center">Créations</th>
                <th className="px-4 py-2.5 text-center">Modifications</th>
                <th className="px-4 py-2.5 text-center">Exports</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {data.activite_par_utilisateur.map((u, i) => (
                <tr key={u.username} className={i % 2 ? 'bg-gray-50 dark:bg-gray-800/60' : ''}>
                  <td className="px-4 py-2.5">
                    <span className="font-medium text-gray-900 dark:text-gray-100">{u.nom || u.username}</span>
                    <span className="text-xs text-gray-400 ml-1">({u.username})</span>
                  </td>
                  <td className="px-4 py-2.5 text-center font-bold text-gray-900 dark:text-gray-100">{u.total}</td>
                  <td className="px-4 py-2.5 text-center text-blue-600">{u.par_type.login || 0}</td>
                  <td className="px-4 py-2.5 text-center text-green-600">{u.par_type.creation || 0}</td>
                  <td className="px-4 py-2.5 text-center text-amber-600">{u.par_type.modification || 0}</td>
                  <td className="px-4 py-2.5 text-center text-purple-600">{u.par_type.export || 0}</td>
                </tr>
              ))}
              {data.activite_par_utilisateur.length === 0 && (
                <tr><td colSpan={6} className="text-center py-6 text-gray-400">Aucune activité</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Activité par jour (graphe barres) */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <h3 className="font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2 mb-4">
          <Eye size={18} /> Activité journalière (7 derniers jours)
        </h3>
        <div className="flex items-end gap-2 h-32">
          {data.activite_par_jour.map(j => {
            const maxCount = Math.max(...data.activite_par_jour.map(d => d.count), 1);
            const pct = (j.count / maxCount) * 100;
            return (
              <div key={j.date} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-[10px] font-bold text-gray-700 dark:text-gray-300">{j.count}</span>
                <div className="w-full bg-blue-500 rounded-t" style={{ height: `${Math.max(pct, 4)}%` }} />
                <span className="text-[9px] text-gray-400">
                  {new Date(j.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                </span>
              </div>
            );
          })}
          {data.activite_par_jour.length === 0 && (
            <p className="text-sm text-gray-400 w-full text-center py-8">Aucune donnée</p>
          )}
        </div>
      </div>

      {/* Historique des connexions */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <h3 className="font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2 mb-4">
          <Shield size={18} /> Historique des connexions
        </h3>
        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700/50">
              <tr className="text-xs text-gray-500 dark:text-gray-400 uppercase">
                <th className="px-4 py-2.5 text-left">Utilisateur</th>
                <th className="px-4 py-2.5 text-left">Date & Heure</th>
                <th className="px-4 py-2.5 text-left">Adresse IP</th>
                <th className="px-4 py-2.5 text-left">Navigateur</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {(showAllConnexions ? data.historique_connexions : data.historique_connexions.slice(0, 20)).map((c, i) => (
                <tr key={i} className={i % 2 ? 'bg-gray-50 dark:bg-gray-800/60' : ''}>
                  <td className="px-4 py-2 font-medium text-gray-900 dark:text-gray-100">{c.nom || c.utilisateur}</td>
                  <td className="px-4 py-2 text-xs text-gray-500">{new Date(c.date).toLocaleString('fr-FR')}</td>
                  <td className="px-4 py-2 text-xs font-mono text-gray-400">{c.ip || '—'}</td>
                  <td className="px-4 py-2 text-xs text-gray-400 max-w-[200px] truncate">{c.user_agent || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.historique_connexions.length > 20 && (
          <button onClick={() => setShowAllConnexions(!showAllConnexions)} className="mt-2 text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400">
            {showAllConnexions ? 'Afficher moins' : `Voir tout (${data.historique_connexions.length})`}
          </button>
        )}
      </div>
    </div>
  );
};

export default SurveillanceTab;
